// src/ImportarFirebase.js
// Copia os dados do Firebase (Firestore) para o banco Firebird do servidor.
// Abre pelo endereço do app com ?importar no final (ex.: http://localhost:3000/?importar).
// Pode ser rodado mais de uma vez: o que já foi copiado é atualizado, sem duplicar.
import React, { useState } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from './firebaseConfig';
import { api } from './api';
import { recarregar } from './dados';

// Timestamp do Firestore -> texto ISO (o resto fica como está)
const limpar = (v) => {
    if (v && typeof v.toDate === 'function') return v.toDate().toISOString();
    if (Array.isArray(v)) return v.map(limpar);
    if (v && typeof v === 'object') {
        const r = {};
        Object.keys(v).forEach((k) => { r[k] = limpar(v[k]); });
        return r;
    }
    return v;
};
const ler = async (caminho) => (await getDocs(collection(db, ...caminho))).docs.map((d) => ({ ...limpar(d.data()), id: d.id }));

const CADASTROS = [
    { nome: 'empresas', titulo: 'Empresas' },
    { nome: 'usuarios', titulo: 'Usuários' },
    { nome: 'consultores', titulo: 'Consultores' },
    { nome: 'modulos', titulo: 'Módulos' },
];
const LOTE = 150;

const ImportarFirebase = ({ onVoltar }) => {
    const [linhas, setLinhas] = useState([]);
    const [rodando, setRodando] = useState(false);
    const [fim, setFim] = useState(null);
    const log = (texto, tipo = 'info') => setLinhas((l) => [...l, { texto, tipo, hora: new Date().toLocaleTimeString('pt-BR') }]);

    const enviar = async (colecao, itens) => {
        let gravados = 0; let historicos = 0; const renumerar = [];
        for (let i = 0; i < itens.length; i += LOTE) {
            const r = await api.post(`/importar/${colecao}`, { itens: itens.slice(i, i + LOTE) });
            gravados += r.gravados; historicos += r.historicos || 0; renumerar.push(...(r.renumerar || []));
        }
        return { gravados, historicos, renumerar };
    };

    const iniciar = async () => {
        if (!window.confirm('Copiar todos os dados do Firebase para o servidor agora?')) return;
        setRodando(true); setLinhas([]); setFim(null);
        try {
            for (const c of CADASTROS) {
                log(`Lendo ${c.titulo} do Firebase...`);
                const itens = await ler([c.nome]);
                const r = await enviar(c.nome, itens);
                log(`${c.titulo}: ${r.gravados} gravado(s).`, 'ok');
            }

            log('Lendo pendências do Firebase...');
            const pendencias = await ler(['tasks']);
            log(`${pendencias.length} pendência(s) encontradas. Lendo os históricos de movimentação...`);
            let feitos = 0;
            const paralelo = 8;
            for (let i = 0; i < pendencias.length; i += paralelo) {
                await Promise.all(pendencias.slice(i, i + paralelo).map(async (p) => {
                    try { p.historico = await ler(['tasks', p.id, 'historicoStatus']); } catch (e) { p.historico = []; }
                }));
                feitos = Math.min(pendencias.length, i + paralelo);
                if (feitos % 200 < paralelo || feitos === pendencias.length) log(`Históricos lidos: ${feitos}/${pendencias.length}`);
            }
            const r = await enviar('pendencias', pendencias);
            log(`Pendências: ${r.gravados} gravada(s), ${r.historicos} movimentação(ões) de histórico.`, 'ok');
            if (r.renumerar.length) log(`${r.renumerar.length} pendência(s) tinham número repetido no Firebase e vão receber número novo.`, 'aviso');

            log('Ajustando a numeração...');
            const f = await api.post('/importar/finalizar');
            (f.renumeradas || []).forEach((x) => log(`Pendência ${x.id} recebeu o nº ${x.nr}.`, 'aviso'));
            setFim(f.totais);
            log('Importação concluída.', 'ok');
            ['empresas', 'usuarios', 'consultores', 'modulos', 'pendencias'].forEach(recarregar);
        } catch (e) {
            console.error(e);
            log(`Erro: ${e.message}`, 'erro');
            if (/quota|resource-exhausted/i.test(e.message)) log('O limite diário de leituras do Firebase acabou. Tente de novo amanhã (o que já foi copiado fica salvo).', 'erro');
        } finally {
            setRodando(false);
        }
    };

    const cor = { info: 'text-gray-700', ok: 'text-green-700', aviso: 'text-amber-700', erro: 'text-red-700 font-semibold' };
    return (
        <div className="max-w-3xl mx-auto p-4 space-y-4">
            <div className="bg-white rounded-lg shadow p-5">
                <h2 className="text-xl font-semibold text-gray-800">Importar dados do Firebase</h2>
                <p className="text-sm text-gray-600 mt-1">
                    Copia empresas, usuários, consultores, módulos e todas as pendências (com o histórico) do Firebase
                    para o banco do servidor. Pode rodar mais de uma vez: nada é duplicado.
                </p>
                <div className="flex gap-2 mt-4">
                    <button onClick={iniciar} disabled={rodando} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm disabled:opacity-60">
                        {rodando ? 'Importando...' : 'Iniciar importação'}
                    </button>
                    <button onClick={onVoltar} disabled={rodando} className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 text-sm">Voltar</button>
                </div>
            </div>
            {fim && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
                    {Object.entries(fim).map(([k, v]) => (
                        <div key={k}><div className="text-gray-500 text-xs">{k.replace('_', ' ')}</div><div className="text-2xl font-bold text-green-800">{v}</div></div>
                    ))}
                </div>
            )}
            {linhas.length > 0 && (
                <div className="bg-white rounded-lg shadow p-4 text-sm font-mono max-h-[50vh] overflow-y-auto space-y-1">
                    {linhas.map((l, i) => <div key={i} className={cor[l.tipo]}><span className="text-gray-400">{l.hora}</span> {l.texto}</div>)}
                </div>
            )}
        </div>
    );
};

export default ImportarFirebase;
